import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, MapPin, Battery, Thermometer, Navigation, Download, Eye, Wifi, Edit2, Check, X, RefreshCw } from 'lucide-react';
import { GpsData, Device, traqPackAPI } from '../lib/supabase';
import { formatDistanceToNow, format } from 'date-fns';
import GpsDetailModal from './GpsDetailModal';
import TemperatureHistoryModal from './TemperatureHistoryModal';

interface DataTableProps {
  data: GpsData[];
  devices: Device[];
  loading: boolean;
  searchTerm: string;
  onRefresh?: () => void;
  refreshing?: boolean;
}

type SortField = keyof GpsData | 'device_name' | 'imei' | 'nickname' | 'location_source' | 'iccid';
type SortDirection = 'asc' | 'desc';

const DataTable: React.FC<DataTableProps> = ({ data, devices, loading, searchTerm, onRefresh, refreshing = false }) => {
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [filterDevice, setFilterDevice] = useState<string>('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedGpsData, setSelectedGpsData] = useState<GpsData | null>(null);
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null);
  const [editingDeviceName, setEditingDeviceName] = useState<string>('');
  const [tempModalOpen, setTempModalOpen] = useState(false);
  const [tempModalDevice, setTempModalDevice] = useState<{
    id: string;
    name: string;
    tempMin?: number | null;
    tempMax?: number | null;
  } | null>(null);

  // Create a map of device info for quick lookup
  const deviceMap = useMemo(() => {
    const map = new Map<string, Device>();
    devices.forEach(device => map.set(device.id, device));
    return map;
  }, [devices]);

  // Sort and filter data
  const sortedAndFilteredData = useMemo(() => {
    let filtered = data;

    // Apply device filter
    if (filterDevice) {
      filtered = filtered.filter(item => item.device_id === filterDevice);
    }

    // Sort data
    const sorted = [...filtered].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortField === 'device_name') {
        aValue = deviceMap.get(a.device_id)?.device_nickname || `Device ${a.device_id}`;
        bValue = deviceMap.get(b.device_id)?.device_nickname || `Device ${b.device_id}`;
      } else if (sortField === 'imei') {
        aValue = a.raw_data?.imei || a.device_id;
        bValue = b.raw_data?.imei || b.device_id;
      } else if (sortField === 'nickname') {
        aValue = deviceMap.get(a.device_id)?.device_nickname || '';
        bValue = deviceMap.get(b.device_id)?.device_nickname || '';
      } else if (sortField === 'iccid') {
        aValue = deviceMap.get(a.device_id)?.iccid || (a.raw_data as any)?.iccid || a.iccid || '';
        bValue = deviceMap.get(b.device_id)?.iccid || (b.raw_data as any)?.iccid || b.iccid || '';
      } else {
        aValue = a[sortField as keyof GpsData];
        bValue = b[sortField as keyof GpsData];
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, sortField, sortDirection, filterDevice, deviceMap]);

  // Pagination
  const totalPages = Math.ceil(sortedAndFilteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedAndFilteredData.slice(startIndex, endIndex);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectItem = (idString: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(idString)) {
      newSelected.delete(idString);
    } else {
      newSelected.add(idString);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === currentData.length) {
      setSelectedItems(new Set());
    } else {
      const allIds = currentData.map(item =>
        item.id !== undefined && item.id !== null
          ? String(item.id)
          : `${item.device_id}-${item.created_at}`
      );
      setSelectedItems(new Set(allIds));
    }
  };

  const handleStartEdit = (deviceId: string, currentName: string) => {
    setEditingDeviceId(deviceId);
    setEditingDeviceName(currentName);
  };

  const handleCancelEdit = () => {
    setEditingDeviceId(null);
    setEditingDeviceName('');
  };

  const handleSaveDeviceName = async () => {
    if (!editingDeviceId || !editingDeviceName.trim()) {
      handleCancelEdit();
      return;
    }

    try {
      await traqPackAPI.updateDevice(editingDeviceId, editingDeviceName.trim());
      // Update local state to reflect the change immediately
      window.location.reload(); // Simple reload to refresh data
      handleCancelEdit();
    } catch (error) {
      console.error('Error updating device name:', error);
      alert('Failed to update device name. Please try again.');
    }
  };

  const handleExportCsv = () => {
    const rows = sortedAndFilteredData;
    if (!rows || rows.length === 0) return;

    const escapeCsv = (value: any): string => {
      if (value === null || value === undefined) return '';
      const s = String(value);
      if (s.includes('"') || s.includes(',') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const headers = [
      'Device',
      'Nickname',
      'IMEI',
      'ICCID',
      'Address',
      'Latitude',
      'Longitude',
      'Time',
      'Source',
      'Battery',
      'Speed',
      'Temperature'
    ];

    const lines: string[] = [];
    lines.push(headers.join(','));

    rows.forEach(item => {
      const device = deviceMap.get(item.device_id);

      // Parse raw_data if it's a string to safely read structured fields
      let parsedRawData: any = null;
      if (typeof item.raw_data === 'string') {
        try {
          parsedRawData = JSON.parse(item.raw_data);
        } catch {
          parsedRawData = null;
        }
      } else {
        parsedRawData = item.raw_data as any;
      }

      const nickname = device?.device_nickname || (item as any).device_nickname || `Device ${item.device_id}`;
      const imei = parsedRawData?.imei || item.device_id;
      const iccid = parsedRawData?.iccid || (item as any)?.iccid || '';
      const address = (item as any).address || '';
      const latitude = item.latitude;
      const longitude = item.longitude;
      const timeIso = new Date(item.created_at).toISOString();
      const source = (item as any).location_source || 'GPS';
      const battery = item.battery_percentage ?? '';
      const speed = item.speed ?? '';
      const temperature = item.temperature ?? '';

      const dataRow = [
        item.device_id,
        nickname,
        imei,
        iccid,
        address,
        latitude,
        longitude,
        timeIso,
        source,
        battery,
        speed,
        temperature
      ].map(escapeCsv).join(',');

      lines.push(dataRow);
    });

    const csvContent = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const datePart = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];
    link.download = `gps_data_${datePart}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const SortButton: React.FC<{ field: SortField; children: React.ReactNode }> = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-left font-medium text-traqpack-gray-700 hover:text-traqpack-gray-900 transition-colors group"
    >
      <span>{children}</span>
      <div className="flex flex-col">
        <ChevronUp 
          className={`w-3 h-3 transition-colors ${
            sortField === field && sortDirection === 'asc' 
              ? 'text-traqpack-yellow' 
              : 'text-traqpack-gray-400 group-hover:text-traqpack-gray-600'
          }`} 
        />
        <ChevronDown 
          className={`w-3 h-3 transition-colors ${
            sortField === field && sortDirection === 'desc' 
              ? 'text-traqpack-yellow' 
              : 'text-traqpack-gray-400 group-hover:text-traqpack-gray-600'
          }`} 
        />
      </div>
    </button>
  );

  if (loading) {
    return (
      <div className="apple-card p-6">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-6">
            <div className="w-32 h-6 bg-traqpack-gray-200 rounded"></div>
            <div className="w-24 h-8 bg-traqpack-gray-200 rounded"></div>
          </div>
          
          <div className="space-y-3">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="h-12 bg-traqpack-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 lg:p-4 w-full max-w-none">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-traqpack-gray-900">GPS Data</h2>
          <p className="text-sm text-traqpack-gray-600 mt-1">
            {sortedAndFilteredData.length} records
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterDevice}
            onChange={(e) => setFilterDevice(e.target.value)}
            className="h-9 px-3 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent"
          >
            <option value="">All Devices</option>
            {devices.map((device) => (
              <option key={device.id} value={device.id}>
                {device.device_nickname ? `${device.device_nickname} (${device.device_id})` : device.device_id}
              </option>
            ))}
          </select>
          
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="h-9 px-3 border border-gray-300 rounded-lg text-sm bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
          
          {onRefresh && (
            <button 
              onClick={onRefresh}
              disabled={refreshing}
              className="h-9 px-4 inline-flex items-center border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-traqpack-yellow disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh GPS data"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          )}
          
          <button onClick={handleExportCsv} className="h-9 px-4 inline-flex items-center border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-traqpack-yellow">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {sortedAndFilteredData.length === 0 ? (
        <div className="text-center py-12">
          <MapPin className="w-12 h-12 text-traqpack-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-traqpack-gray-700 mb-2">No Data Found</h3>
          <p className="text-traqpack-gray-600">
            {searchTerm 
              ? `No records match your search for "${searchTerm}"`
              : 'No GPS data available for the selected filters.'
            }
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 lg:mx-0">
            <table className="w-full min-w-[1200px]">
              <thead>
                <tr className="border-b border-traqpack-gray-200 bg-gray-50">
                  <th className="text-left py-2 px-2 lg:px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={selectedItems.size === currentData.length && currentData.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-traqpack-gray-300 text-traqpack-yellow focus:ring-traqpack-yellow"
                    />
                  </th>
                  <th className="text-left py-2 px-2 lg:px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-48">
                    <SortButton field="device_name">Device</SortButton>
                  </th>
                  <th className="text-left py-2 px-2 lg:px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-36 hidden lg:table-cell">
                    <SortButton field="nickname">Nickname</SortButton>
                  </th>
                  <th className="text-left py-2 px-2 lg:px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-40 hidden xl:table-cell">
                    <SortButton field="imei">IMEI</SortButton>
                  </th>
                  <th className="text-left py-2 px-2 lg:px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-32 hidden xl:table-cell">
                    <SortButton field="iccid">ICCID</SortButton>
                  </th>

                  <th className="text-left py-2 px-2 lg:px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-1/3">
                    <SortButton field="latitude">Location</SortButton>
                  </th>
                  <th className="text-left py-2 px-3 lg:px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                    <SortButton field="created_at">Time</SortButton>
                  </th>
                  <th className="text-left py-2 px-2 lg:px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-24 hidden md:table-cell">
                    <SortButton field="location_source">Source</SortButton>
                  </th>
                  <th className="text-left py-2 px-2 lg:px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">Status</th>
                  <th className="text-left py-2 px-2 lg:px-3 text-xs font-semibold text-gray-700 uppercase tracking-wider w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => {
                  const device = deviceMap.get(item.device_id);
                  const rowId = (item.id !== undefined && item.id !== null)
                    ? String(item.id)
                    : `${item.device_id}-${item.created_at}`;
                  const isSelected = selectedItems.has(rowId);
                  
                  return (
                    <motion.tr
                      key={String(item.id)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={`border-b border-traqpack-gray-100 hover:bg-traqpack-gray-50 transition-colors text-sm ${
                        isSelected ? 'bg-traqpack-yellow bg-opacity-10' : ''
                      }`}
                    >
                      <td className="py-2 px-2 lg:px-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectItem(rowId)}
                          className="rounded border-traqpack-gray-300 text-traqpack-yellow focus:ring-traqpack-yellow"
                        />
                      </td>
                      
                      <td className="py-2 px-2 lg:px-3">
                        <div>
                          {editingDeviceId === item.device_id ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={editingDeviceName}
                                onChange={(e) => setEditingDeviceName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveDeviceName();
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                                className="px-2 py-1 border border-traqpack-gray-300 rounded-md text-sm focus:ring-2 focus:ring-traqpack-yellow focus:border-transparent"
                                autoFocus
                              />
                              <button
                                onClick={handleSaveDeviceName}
                                className="p-1 hover:bg-green-100 rounded-md transition-colors"
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-1 hover:bg-red-100 rounded-md transition-colors"
                              >
                                <X className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          ) : (
                            <div className="group">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-traqpack-gray-900">
                                  {device?.device_nickname || item.device_nickname || `Device ${item.device_id}`}
                                </span>
                                <button
                                  onClick={() => handleStartEdit(item.device_id, device?.device_nickname || item.device_nickname || `Device ${item.device_id}`)}
                                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-traqpack-gray-100 rounded-md transition-all"
                                >
                                  <Edit2 className="w-3 h-3 text-traqpack-gray-600" />
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="text-sm text-traqpack-gray-600">
                            ID: {item.device_id}
                          </div>
                        </div>
                      </td>
                      
                      <td className="py-3 px-2 lg:px-4 hidden lg:table-cell">
                        <span className="text-sm text-traqpack-gray-700">
                          {device?.device_nickname || '-'}
                        </span>
                      </td>
                      
                      <td className="py-3 px-2 lg:px-4 hidden xl:table-cell">
                        <span className="text-sm font-mono text-traqpack-gray-700">
                          {item.raw_data?.imei || item.device_id}
                        </span>
                      </td>
                      
                      <td className="py-3 px-2 lg:px-4 hidden xl:table-cell">
                        <span className="text-sm font-mono text-traqpack-gray-700">
                          {(() => {
                            // Parse raw_data if it's a string
                            let parsedRawData = null;
                            if (typeof item.raw_data === 'string') {
                              try {
                                parsedRawData = JSON.parse(item.raw_data);
                              } catch (e) {
                                console.error('Error parsing raw_data:', e);
                              }
                            } else {
                              parsedRawData = item.raw_data;
                            }
                            
                            const iccid = parsedRawData?.iccid || item.raw_data?.iccid || '-';
                            
                            // Debug logging for specific device
                            if (item.device_id === '251075013057') {
                              console.log('🔍 DataTable ICCID Debug for 251075013057:', {
                                device_id: item.device_id,
                                raw_data: item.raw_data,
                                parsed_raw_data: parsedRawData,
                                raw_data_iccid: item.raw_data?.iccid,
                                parsed_iccid: parsedRawData?.iccid,
                                final_iccid: iccid
                              });
                            }
                            return iccid;
                          })()}
                        </span>
                      </td>
                      

                      
                      <td className="py-2 px-2 lg:px-3">
                        <div className="max-w-md">
                          <div className="text-sm text-traqpack-gray-900 font-medium leading-tight" title={item.address || 'Address not available'}>
                            {item.address || 'Address not available'}
                          </div>
                          <div className="text-xs text-traqpack-gray-600 font-mono mt-1">
                            {item.latitude.toFixed(6)}, {item.longitude.toFixed(6)}
                          </div>
                        </div>
                      </td>
                      
                      <td className="py-2 px-3 lg:px-4 min-w-[140px]">
                        <div>
                          <div className="text-sm text-traqpack-gray-900 font-medium">
                            {format(new Date(item.created_at), 'MMM dd, HH:mm:ss')}
                          </div>
                          <div className="text-xs text-traqpack-gray-600">
                            {formatDistanceToNow(new Date(item.created_at))} ago
                          </div>
                        </div>
                      </td>
                      
                      <td className="py-3 px-2 lg:px-4 hidden md:table-cell">
                        <div className="flex items-center space-x-1">
                          <Wifi className="w-3 h-3 text-traqpack-gray-500" />
                          <span className="text-sm text-traqpack-gray-700">
                            {item.location_source || 'GPS'}
                          </span>
                        </div>
                      </td>
                      
                      <td className="py-2 px-2 lg:px-3">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-2 space-y-1 lg:space-y-0 text-xs">
                          {item.battery_percentage && (
                            <div className="flex items-center space-x-1 text-green-600">
                              <Battery className="w-3 h-3" />
                              <span className="font-medium">{item.battery_percentage}%</span>
                            </div>
                          )}
                          {item.temperature && (
                            <button
                              type="button"
                              onClick={() => {
                                const device = deviceMap.get(item.device_id);
                                const name =
                                  device?.device_nickname ||
                                  item.device_nickname ||
                                  `Device ${item.device_id}`;
                                const tempMin =
                                  (device as any)?.temperature_min ??
                                  (device as any)?.temp_min ??
                                  null;
                                const tempMax =
                                  (device as any)?.temperature_max ??
                                  (device as any)?.temp_max ??
                                  null;
                                setTempModalDevice({
                                  id: item.device_id,
                                  name,
                                  tempMin,
                                  tempMax,
                                });
                                setTempModalOpen(true);
                              }}
                              className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 hover:underline focus:outline-none"
                              title="View temperature history"
                            >
                              <Thermometer className="w-3 h-3" />
                              <span>{item.temperature}°C</span>
                            </button>
                          )}
                          {item.speed > 0 && (
                            <div className="flex items-center space-x-1 text-purple-600">
                              <Navigation className="w-3 h-3" />
                              <span>{item.speed} km/h</span>
                            </div>
                          )}
                        </div>
                      </td>
                      
                      <td className="py-2 px-2 lg:px-3">
                        <button
                          onClick={() => {
                            setSelectedGpsData(item);
                            setDetailModalOpen(true);
                          }}
                          className="apple-button apple-button-secondary text-xs px-2 py-1"
                          title="View details"
                        >
                          <Eye className="w-3 h-3 lg:mr-1" />
                          <span className="hidden lg:inline">View</span>
                        </button>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-traqpack-gray-200">
              <div className="text-sm text-traqpack-gray-600">
                Showing {startIndex + 1} to {Math.min(endIndex, sortedAndFilteredData.length)} of {sortedAndFilteredData.length} results
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                  className="apple-button apple-button-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const page = currentPage <= 3 ? index + 1 : currentPage - 2 + index;
                    if (page > totalPages) return null;
                    
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 text-sm rounded-lg transition-colors ${
                          page === currentPage
                            ? 'bg-traqpack-yellow text-traqpack-gray-900'
                            : 'text-traqpack-gray-600 hover:bg-traqpack-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                  className="apple-button apple-button-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* GPS Detail Modal */}
      <GpsDetailModal
        data={selectedGpsData}
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedGpsData(null);
        }}
      />

      {/* Temperature History Modal */}
      <TemperatureHistoryModal
        isOpen={tempModalOpen}
        deviceId={tempModalDevice?.id || null}
        deviceName={tempModalDevice?.name}
        tempMin={tempModalDevice?.tempMin ?? undefined}
        tempMax={tempModalDevice?.tempMax ?? undefined}
        onClose={() => {
          setTempModalOpen(false);
          setTempModalDevice(null);
        }}
      />
    </div>
  );
};

export default DataTable;